import styled from '@emotion/styled';
import {
  useCurrentSession,
  useFoundCollaborator,
} from '@ferreri/minato-auth-react';
import {
  Accordion,
  AddCircleOutlineMediumIcon,
  Card,
  CheckCircleOutlineMediumIcon,
  ContentCompact,
  HeaderFunnel,
  InfoBanner,
  Loading,
  RowCenterBetween,
  Section,
  TextCaption,
  TextHeading2,
  TextHighlight,
  TextParagraph,
  TitleAndDescription,
  token,
  UnorderedList,
} from '@ferreri/prever-component-lib';
import { PageLayout } from '@ferreri/prever-component-lib/nextjs';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { SETTINGS } from '../../config/settings';
import { formatCentsMonetary } from '../../utils/formatCentsMonetary';
import {
  useCurrentPlanVersionManifest,
  useGroupedBenefitItemsFromPlanVersion,
} from '../admin/plan-versions/plan-version.store';
import { BenefitItem } from '../admin/plan-versions/plan-version.type';
import { LogoMap } from '../catalog/LogoMap';
import { useCreateOccurrence } from '../event/event.store';
import { useAllBenefits } from '../plan-benefit/plan-benefit.store';
import { Benefit } from '../plan-benefit/plan-benefit.types';
import { HolderRule } from './HolderRule';
import { InsideUnorderedList } from './PlanCoverages';
import { DependentRule } from './dependents-rule/DependentRule';
import { PlanDetailsPrimaryButton } from './PlanDetailsPrimaryButton';

const portalURL = `${SETTINGS.public.urlSaleWeb}/portal/plans`;

export const PlanDetails = () => {
  const router = useRouter();
  const { data: collaborator } = useFoundCollaborator();
  const isCollaborator = collaborator?.found;
  const { data: planVersion, isLoading: isLoadingPlanVersion } =
    useCurrentPlanVersionManifest();
  const createOccurrenceRef = useRef(false);
  const { mutate } = useCreateOccurrence();
  const { data: session } = useCurrentSession();

  const slug = planVersion?.plan_distribution?.plan?.slug;
  const basePrice = planVersion?.subscription_rule?.base_value;
  const { data: benefits, isLoading: isLoadingBenefits } = useAllBenefits();
  const isLoading = isLoadingPlanVersion || isLoadingBenefits;
  const name = planVersion?.plan_distribution?.plan?.name;
  const planName = `Plano ${name}`;
  const isNeo = slug == `neo`;
  const isAngeplus = planVersion?.contract_provider === 'angeplus';

  const transfer = planVersion?.benefit_rules?.find(
    (data) => data?.benefit?.name == 'Translado',
  );

  const mappedBenefits = benefits?.map((data) => {
    let aditional = '';
    if (data?.name == 'Translado') {
      aditional =
        transfer?.coverage_region === 'state'
          ? `Translado funerário livre no estado de residência do titular.`
          : `Translado incluso em raio de ${transfer?.coverage_radius}km de distância.`;
    }

    return { ...data, aditional: aditional };
  });

  const groupedBenefitItems = useGroupedBenefitItemsFromPlanVersion(
    mappedBenefits,
    planVersion,
  );

  const handleRedirect = () => {
    isCollaborator ? window.location.replace(portalURL) : router.replace('/');
  };

  useEffect(() => {
    if (
      createOccurrenceRef.current === false &&
      session?.identity?.id &&
      planVersion
    ) {
      mutate({
        event_type_slug: 'access_plan_details',
        event_params: {
          plan_version_id: planVersion?.id,
          plan_version_slug: planVersion?.slug,
          plan_version_external_id: planVersion?.external_id,
        },
      });
      createOccurrenceRef.current = true;
    }
  }, [createOccurrenceRef.current, session?.identity?.id, planVersion]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <PageLayout>
      <ContentCompact>
        <HeaderFunnel onClickArrow={handleRedirect}>{planName}</HeaderFunnel>
        <Section>
          <TitleAndDescription
            title="Benefícios inclusos no valor"
            description="Incluídos nesse plano, sem custo adicional."
          />
          <Accordion
            items={groupedBenefitItems?.active?.map((item: BenefitItem) => ({
              title: item.name,
              content: <BenefitAccordionContent benefit={item} />,
              icon: (
                <CheckCircleOutlineMediumIcon
                  color={token.colors.feedbackSuccessPrimary}
                />
              ),
            }))}
            openItem={0}
          />
        </Section>
        {Boolean(groupedBenefitItems.optional.length) && (
          <Section>
            <TitleAndDescription
              title="Benefícios disponíveis por valor adicional"
              description="Inclua no plano por custo adicional. Você pode seguir com a simulação para calcular o valor de cada benefício."
            />
            <Accordion
              items={groupedBenefitItems?.optional?.map(
                (item: BenefitItem) => ({
                  title: item.name,
                  content: <BenefitAccordionContent benefit={item} />,
                  icon: (
                    <AddCircleOutlineMediumIcon
                      color={token.colors.accentPrimary}
                    />
                  ),
                }),
              )}
            />
          </Section>
        )}
        <HolderRule planVersion={planVersion} />
        <DependentRule
          dependent_rule={planVersion?.dependent_rule}
          subscription_rule={planVersion?.subscription_rule}
        />
        <Section>
          <TextHeading2>Mensalidade</TextHeading2>
          <Card>
            <RowCenterBetween>
              {LogoMap[slug]}
              <PriceSection>
                <TextCaption>a partir de</TextCaption>
                <TextHighlight>
                  {`${formatCentsMonetary(basePrice)}`}
                </TextHighlight>
              </PriceSection>
            </RowCenterBetween>
            {!isNeo && (
              <InfoBanner>
                <TextCaption>
                  Com 5% de desconto no cartão de crédito recorrente.
                </TextCaption>
              </InfoBanner>
            )}
            <TextParagraph>
              {isAngeplus
                ? 'Inicie a contratação deste plano e inclua seus dependentes e benefícios opcionais a seguir.'
                : 'Simule a contratação deste plano incluindo dependentes e os benefícios opcionais.'}
            </TextParagraph>
            <PlanDetailsPrimaryButton
              planVersionId={planVersion?.id}
              contractProvider={planVersion?.contract_provider}
            />
          </Card>
        </Section>
      </ContentCompact>
    </PageLayout>
  );
};

type BenefitAccordionContentProps = {
  benefit: Benefit;
};

const BenefitAccordionContent = (props: BenefitAccordionContentProps) => {
  const details = props.benefit?.details?.map((detail) => detail.text);

  return (
    <Section>
      <DescriptionSection>
        {Boolean(props.benefit?.description) && (
          <TextParagraph>{props.benefit?.description}</TextParagraph>
        )}
        {Boolean(props.benefit?.aditional) && (
          <TextParagraph>{props.benefit?.aditional}</TextParagraph>
        )}
      </DescriptionSection>

      {Boolean(details?.length) && (
        <InsideUnorderedList>
          <UnorderedList items={details} />
        </InsideUnorderedList>
      )}
    </Section>
  );
};

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingXxSmall};
  width: 100%;
`;
